import type {
  FunnelTemplate,
  GeneratedFunnelTemplate,
} from "schemas/generated/funnelTemplate";

import React, { useEffect, useMemo, useState } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import previewPage from "@/features/funnel/assets/preview-page.png";
import Button from "@replo/design-system/components/button/Button";
import { skipToken } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import FunnelCanvas from "./components/FunnelCanvas";
import NewFunnelMenu from "./components/NewFunnelMenu";
import { useCreateFunnel } from "./hooks/useCreateFunnel";

export type TemplateItem = FunnelTemplate | GeneratedFunnelTemplate;

function EmptyFunnelPreview() {
  return (
    <div className="h-full w-full border-0.5 border-border rounded-lg flex items-center justify-center bg-neutral-soft">
      <div className="text-center flex flex-col items-center gap-2">
        <img src={previewPage} alt="preview page" className="w-[150px]" />
        <div className="text-sm font-medium">Select a template to preview</div>
      </div>
    </div>
  );
}

export default function NewFunnelPage() {
  const projectId = useCurrentProjectId();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { handleCreateFunnel, isLoading: isCreatingFunnel } = useCreateFunnel();

  const [selectedFunnelTemplate, setSelectedFunnelTemplate] =
    useState<TemplateItem | null>(null);

  const { data: funnelTemplates } = trpc.funnelTemplate.find.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const {
    mutate: createFunnelFromGeneratedTemplate,
    isPending: isCreatingFunnelFromGeneratedTemplate,
  } = trpc.funnel.createFunnelFromGeneratedTemplate.useMutation({
    onSuccess: (createdFunnel) => {
      void trpcUtils.funnel.find.invalidate();
      void trpcUtils.funnel.list.invalidate({ projectId });
      void trpcUtils.webContainer.getFileTree.invalidate({
        projectId,
      });
      navigate(`/shops/${projectId}/funnels/${createdFunnel.id}`);
    },
  });

  // Handle initial template selection from URL parameter
  useEffect(() => {
    const initialSelectedTemplateId = searchParams.get(
      "initialSelectedTemplateId",
    );

    const shouldSelectTemplate =
      initialSelectedTemplateId && funnelTemplates && !selectedFunnelTemplate;

    if (shouldSelectTemplate) {
      const template = funnelTemplates.find(
        (t) => t.id === initialSelectedTemplateId,
      );
      if (template) {
        setSelectedFunnelTemplate(template);
        // Remove the parameter from URL after selecting
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("initialSelectedTemplateId");
        setSearchParams(newSearchParams, { replace: true });
      }
    }
  }, [funnelTemplates, searchParams, setSearchParams, selectedFunnelTemplate]);

  const handleFunnelCreation = async (template: TemplateItem | null) => {
    if (!template || !projectId) {
      return;
    }

    const isFunnelTemplate =
      template.funnelTemplateSteps.some((step) => "fileTemplate" in step) &&
      Array.isArray(template.funnelTemplateSteps);

    if (isFunnelTemplate) {
      void handleCreateFunnel({
        projectId,
        name: template.name,
        funnelTemplateId: template.id,
      });
    } else {
      // Generated funnel template
      void createFunnelFromGeneratedTemplate({
        projectId,
        template: template as GeneratedFunnelTemplate,
      });
    }
  };

  const previewFunnelPages = useMemo(() => {
    if (selectedFunnelTemplate) {
      return selectedFunnelTemplate.funnelTemplateSteps.map((step, index) => {
        const templateId =
          "fileTemplate" in step ? step.fileTemplate.id : step.id;
        const name =
          "fileTemplate" in step ? step.fileTemplate.name : step.name;

        return {
          pageRoute: `/template-preview/${templateId}`,
          index,
          name,
          id: templateId,
        };
      });
    }

    return [];
  }, [selectedFunnelTemplate]);

  const isCreatingFunnelState =
    isCreatingFunnelFromGeneratedTemplate || isCreatingFunnel;

  return (
    <>
      <div className="flex h-full">
        <div className="w-1/3 h-full overflow-y-auto no-scrollbar border-t-0.5 border-border py-3">
          <NewFunnelMenu
            selectedFunnelTemplate={selectedFunnelTemplate}
            setSelectedFunnelTemplate={setSelectedFunnelTemplate}
          />
        </div>
        <div className="w-2/3 ml-3 pb-3 h-full">
          {previewFunnelPages && previewFunnelPages.length > 0 ? (
            <FunnelCanvas funnelPages={previewFunnelPages} isPreview />
          ) : (
            <EmptyFunnelPreview />
          )}
        </div>
      </div>

      {selectedFunnelTemplate && (
        <div className="absolute bottom-0 left-0 w-full h-16 bg-white flex gap-2 justify-end items-center px-6 fade-in border-t-0.5 border-border z-[2147480003]">
          <Button
            variant="secondary"
            size="base"
            onClick={() => {
              setSelectedFunnelTemplate(null);
            }}
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            size="base"
            onClick={() => {
              void handleFunnelCreation(selectedFunnelTemplate);
            }}
            isLoading={isCreatingFunnelState}
            disabled={isCreatingFunnelState}
          >
            {selectedFunnelTemplate ? "Select Funnel" : "Create Funnel"}
          </Button>
        </div>
      )}
    </>
  );
}
